import { useState, useEffect, lazy, Suspense } from "react";
import { Link, useLocation } from "wouter";
import { prefetchRoute } from "@/lib/prefetch";
import { MapPin, Globe, UserCircle, Mountain, Calculator, Calendar, Menu, X, Heart, LogOut, ChevronDown, BookOpen, Tent, Anchor, ShieldCheck, CalendarDays, Music, Route, Sun, Moon, Search, Video, Map, Footprints, Loader2, TrendingUp, Thermometer, Briefcase, Package, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SearchModal } from "@/components/shared/SearchModal";
import { useAppStore } from "@/store/use-app-store";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
const AuthModal = lazy(() => import("@/components/shared/AuthModal").then(m => ({ default: m.AuthModal })));
import { queryClient } from "@/lib/queryClient";
import { localePath, switchLangPath, SiteLang, LANG_CONFIGS } from "@/lib/locale";

const PRIMARY_NAV = [
  { href: "/", icon: MapPin, labels: { en: "Destinations", de: "Reiseziele", fr: "Destinations", it: "Destinazioni", nl: "Bestemmingen", hr: "Odredišta", pl: "Miejsca" }, testId: "link-nav-destinations" },
  { href: "/itinerary-guides", icon: Route, labels: { en: "Itineraries", de: "Reiserouten", fr: "Itinéraires", it: "Itinerari", nl: "Reisroutes", hr: "Itinereri", pl: "Trasy" }, testId: "link-nav-itinerary-guides" },
  { href: "/national-parks", icon: Mountain, labels: { en: "National Parks", de: "Nationalparks", fr: "Parcs nationaux", it: "Parchi nazionali", nl: "Nationale parken", hr: "Nacionalni parkovi", pl: "Parki Narodowe" }, testId: "link-nav-national-parks" },
  { href: "/best-time", icon: Calendar, labels: { en: "Best Time", de: "Beste Reisezeit", fr: "Meilleure période", it: "Miglior periodo", nl: "Beste reistijd", hr: "Najbolje vrijeme", pl: "Najlepszy czas" }, testId: "link-nav-best-time" },
  { href: "/budget-calculator", icon: Calculator, labels: { en: "Budget", de: "Budget", fr: "Budget", it: "Budget", nl: "Budget", hr: "Budžet", pl: "Budżet" }, testId: "link-nav-budget-calculator" },
  { href: "/smart-score", icon: TrendingUp, labels: { en: "Smart Score", de: "Smart Score", fr: "Smart Score", it: "Smart Score", nl: "Smart Score", hr: "Smart Score", pl: "Smart Score" }, testId: "link-nav-smart-score" },
];

const MORE_NAV = [
  { href: "/webcams", icon: Video, labels: { en: "Live Webcams", de: "Live Webcams", fr: "Webcams Live", it: "Webcam Live", nl: "Live Webcams", hr: "Webcam uživo", pl: "Kamery na żywo" }, testId: "link-nav-webcams" },
  { href: "/map", icon: Map, labels: { en: "Map Explorer", de: "Karten-Explorer", fr: "Carte Explorer", it: "Esplora Mappa", nl: "Kaart Verkenner", hr: "Istraži kartu", pl: "Mapa" }, testId: "link-nav-map" },
  { href: "/hiking", icon: Footprints, labels: { en: "Hiking & Trails", de: "Wandern & Routen", fr: "Randonnée", it: "Escursioni", nl: "Wandelen", hr: "Planinarenje", pl: "Piesze szlaki" }, testId: "link-nav-hiking" },
  { href: "/camping", icon: Tent, labels: { en: "Camping", de: "Camping", fr: "Camping", it: "Camping", nl: "Kamperen", hr: "Kampiranje", pl: "Kemping" }, testId: "link-nav-camping" },
  { href: "/sailing", icon: Anchor, labels: { en: "Sailing", de: "Segeln", fr: "Voile", it: "Vela", nl: "Zeilen", hr: "Jedrenje", pl: "Żeglarstwo" }, testId: "link-nav-sailing" },
  { href: "/festivals", icon: Music, labels: { en: "Events", de: "Events", fr: "Événements", it: "Eventi", nl: "Evenementen", hr: "Događanja", pl: "Wydarzenia" }, testId: "link-nav-festivals" },
  { href: "/croatia-sea-temperature", icon: Thermometer, labels: { en: "Sea Temperature", de: "Meerestemperatur", fr: "Température mer", it: "Temperatura mare", nl: "Zeetemperatuur", hr: "Temperatura mora", pl: "Temperatura morza" }, testId: "link-nav-sea-temperature" },
  { href: "/guides", icon: BookOpen, labels: { en: "Guides", de: "Reiseführer", fr: "Guides", it: "Guide", nl: "Reisgidsen", hr: "Vodiči", pl: "Przewodniki" }, testId: "link-nav-guides" },
  { href: "/blog", icon: BookOpen, labels: { en: "Travel Blog", de: "Reiseblog", fr: "Blog Voyage", it: "Blog Viaggi", nl: "Reisblog", hr: "Blog o putovanjima", pl: "Blog podróżniczy" }, testId: "link-nav-blog" },
  { href: "/itinerary", icon: CalendarDays, labels: { en: "My Travel Plan", de: "Mein Reiseplan", fr: "Mon plan de voyage", it: "Il mio piano", nl: "Mijn reisplan", hr: "Moj plan putovanja", pl: "Mój plan podróży" }, testId: "link-nav-itinerary" },
  { href: "/packing-list", icon: Package, labels: { en: "Packing List", de: "Packliste", fr: "Liste de bagages", it: "Lista valigia", nl: "Paklijst", hr: "Lista pakiranja", pl: "Lista pakowania" }, testId: "link-nav-packing-list" },
  { href: "/media-kit", icon: Briefcase, labels: { en: "Media Kit", de: "Medienkit", fr: "Kit Médias", it: "Media Kit", nl: "Media Kit", hr: "Media Kit", pl: "Media Kit" }, testId: "link-nav-media-kit" },
  { href: "/impressum", icon: FileText, labels: { en: "Impressum", de: "Impressum", fr: "Impressum", it: "Impressum", nl: "Impressum", hr: "Impressum", pl: "Impressum" }, testId: "link-nav-impressum" },
];

type UiStringSet = { signIn: string; signOut: string; myFavourites: string; more: string; openMenu: string; closeMenu: string; home: string };
const UI_STRINGS: Partial<Record<SiteLang, UiStringSet>> & { en: UiStringSet } = {
  en: { signIn: "Sign In", signOut: "Sign Out", myFavourites: "My Favourites", more: "More", openMenu: "Open navigation menu", closeMenu: "Close navigation menu", home: "Smart Croatia — Home" },
  de: { signIn: "Anmelden", signOut: "Abmelden", myFavourites: "Favoriten", more: "Mehr", openMenu: "Menü öffnen", closeMenu: "Menü schließen", home: "Smart Croatia — Startseite" },
  fr: { signIn: "Se connecter", signOut: "Se déconnecter", myFavourites: "Mes favoris", more: "Plus", openMenu: "Ouvrir le menu", closeMenu: "Fermer le menu", home: "Smart Croatia — Accueil" },
  it: { signIn: "Accedi", signOut: "Disconnettiti", myFavourites: "I miei preferiti", more: "Altro", openMenu: "Apri il menu", closeMenu: "Chiudi il menu", home: "Smart Croatia — Home" },
  nl: { signIn: "Inloggen", signOut: "Uitloggen", myFavourites: "Mijn favorieten", more: "Meer", openMenu: "Menu openen", closeMenu: "Menu sluiten", home: "Smart Croatia — Startpagina" },
  hr: { signIn: "Prijava", signOut: "Odjava", myFavourites: "Moji favoriti", more: "Više", openMenu: "Otvori izbornik", closeMenu: "Zatvori izbornik", home: "Smart Croatia — Početna" },
  pl: { signIn: "Zaloguj się", signOut: "Wyloguj się", myFavourites: "Moje ulubione", more: "Więcej", openMenu: "Otwórz menu", closeMenu: "Zamknij menu", home: "Smart Croatia — Strona główna" },
};

const LANG_OPTIONS: { code: SiteLang; label: string; short: string; flag: string }[] = LANG_CONFIGS.map(l => ({
  code: l.code,
  label: l.label,
  short: l.short,
  flag: l.flag,
}));

const PRIMARY_GROUP_LABEL: Partial<Record<SiteLang, string>> & { en: string } = {
  en: "Explore", de: "Entdecken", fr: "Explorer", it: "Esplora", nl: "Verkennen", hr: "Istraži", pl: "Odkrywaj",
};
const MORE_GROUP_LABEL: Partial<Record<SiteLang, string>> & { en: string } = {
  en: "Activities & Tools", de: "Aktivitäten & Tools", fr: "Activités & Outils",
  it: "Attività & Strumenti", nl: "Activiteiten & Tools", hr: "Aktivnosti i alati", pl: "Aktywności i narzędzia",
};

export function Navbar() {
  const { language, user, logout, authModalOpen, setAuthModalOpen, theme, toggleTheme, pageAlternates } = useAppStore();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [switchingLang, setSwitchingLang] = useState(false);
  const [location, navigate] = useLocation();
  const ui = UI_STRINGS[language] ?? UI_STRINGS.en;

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.key === "/" && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === "Escape") {
        setMobileOpen(false);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const handleLogout = () => {
    logout();
    queryClient.invalidateQueries({ queryKey: ["/api/favorites"] });
  };

  const handleLanguageSwitch = (targetLang: SiteLang) => {
    if (targetLang === language) return;
    setSwitchingLang(true);
    // If the current page registered localized alternate URLs (e.g. blog posts
    // with translated slugs), use those directly. Otherwise fall back to the
    // generic path transform which only handles segment translation, not slugs.
    const alternate = pageAlternates?.[targetLang];
    if (alternate) {
      window.location.href = alternate;
    } else {
      window.location.href = switchLangPath(location, targetLang);
    }
  };

  const isActive = (href: string) => {
    const full = localePath(href, language);
    return location === full || location === href;
  };

  const currentLang = LANG_OPTIONS.find(o => o.code === language);

  return (
    <>
      <nav aria-label="Main navigation" className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/80 backdrop-blur-xl">
        <div className="container mx-auto px-4 h-14 flex items-center gap-2">

          {/* Logo */}
          <Link
            href={localePath("/", language)}
            className="flex items-center gap-2 group shrink-0 mr-2"
            data-testid="link-home"
            aria-label={ui.home}
          >
            <div className="bg-primary/10 p-1.5 rounded-md group-hover:bg-primary/20 transition-colors">
              <MapPin className="w-4 h-4 text-primary" aria-hidden="true" />
            </div>
            <span className="font-display font-bold text-base tracking-tight hidden sm:block" data-testid="text-brand-name">
              Smart Croatia
            </span>
          </Link>

          {/* Separator */}
          <div className="hidden xl:block h-5 w-px bg-border/60 mr-1" />

          {/* Primary nav links — xl+ */}
          <div className="hidden xl:flex items-center gap-0.5 flex-1 min-w-0">
            {PRIMARY_NAV.map((link) => {
              const Icon = link.icon;
              const href = localePath(link.href, language);
              const active = isActive(link.href);
              return (
                <Link
                  key={link.href}
                  href={href}
                  onMouseEnter={() => prefetchRoute(link.href)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                    active
                      ? "text-sky-700 dark:text-primary bg-sky-50 dark:bg-primary/10"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                  }`}
                  data-testid={link.testId}
                >
                  <Icon className="w-3.5 h-3.5 shrink-0" />
                  {link.labels[language] ?? link.labels.en}
                </Link>
              );
            })}

            {/* More dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1 text-sm font-medium text-muted-foreground hover:text-foreground px-2.5"
                  data-testid="button-nav-more"
                >
                  {ui.more}
                  <ChevronDown className="w-3.5 h-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="rounded-md w-48 z-[60]">
                {MORE_NAV.map((link) => {
                  const Icon = link.icon;
                  const href = localePath(link.href, language);
                  const active = isActive(link.href);
                  return (
                    <DropdownMenuItem
                      key={link.href}
                      asChild
                      className={`cursor-pointer gap-2 ${active ? "text-primary font-semibold" : ""}`}
                      onMouseEnter={() => prefetchRoute(link.href)}
                      data-testid={link.testId}
                    >
                      <Link href={href}>
                        <Icon className="w-4 h-4 shrink-0" />
                        {link.labels[language] ?? link.labels.en}
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Spacer on mobile/tablet to push actions right */}
          <div className="flex-1 xl:hidden" />

          {/* Right-side actions */}
          <div className="flex items-center gap-1">

            {/* Search */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
              data-testid="button-nav-search"
              className="rounded-full w-8 h-8 text-muted-foreground hover:text-foreground"
            >
              <Search className="w-4 h-4" />
            </Button>

            {/* Theme toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              data-testid="button-theme-toggle"
              className="rounded-full w-8 h-8 text-muted-foreground hover:text-foreground"
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>

            {/* Language selector — always visible */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  aria-label={`Language: ${currentLang?.label ?? language}`}
                  className="flex gap-1.5 px-2.5 h-8 rounded-full border-border/60 text-foreground hover:border-primary/40 hover:bg-primary/5 font-medium"
                  data-testid="button-language-toggle"
                >
                  {switchingLang ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
                  ) : (
                    <span className="text-base leading-none" aria-hidden="true">{currentLang?.flag}</span>
                  )}
                  <span className="text-xs font-bold uppercase hidden sm:inline" aria-hidden="true">{currentLang?.short ?? language}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-md min-w-[160px]">
                {LANG_OPTIONS.map((opt) => (
                  <DropdownMenuItem
                    key={opt.code}
                    onClick={() => handleLanguageSwitch(opt.code)}
                    className={`cursor-pointer gap-2.5 ${language === opt.code ? "font-semibold text-primary bg-primary/5" : ""}`}
                  >
                    <span className="text-base">{opt.flag}</span>
                    <span className="flex-1">{opt.label}</span>
                    <span className="text-xs font-mono text-muted-foreground">{opt.short}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User / Sign In — hidden on mobile */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="hidden sm:flex rounded-full gap-1.5 border-primary/20 px-2.5"
                    data-testid="button-user-menu"
                  >
                    <UserCircle className="w-4 h-4 text-primary shrink-0" />
                    <span className="max-w-[80px] truncate text-sm">{user.email.split("@")[0]}</span>
                    <ChevronDown className="w-3 h-3 text-muted-foreground shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-md w-48">
                  <div className="px-3 py-2">
                    <p className="text-xs font-semibold text-foreground truncate">{user.email}</p>
                    <p className="text-xs text-muted-foreground capitalize">{user.role} account</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="cursor-pointer gap-2"
                    onSelect={() => navigate(localePath("/favorites", language))}
                    data-testid="link-nav-favorites"
                  >
                    <Heart className="w-4 h-4 text-red-500" />
                    {ui.myFavourites}
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="cursor-pointer gap-2"
                        onSelect={() => navigate("/admin")}
                        data-testid="link-nav-admin"
                      >
                        <ShieldCheck className="w-4 h-4 text-amber-500" />
                        Admin Panel
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="cursor-pointer gap-2"
                        onSelect={() => navigate("/ai-cms/dashboard")}
                        data-testid="link-nav-cms"
                      >
                        <BookOpen className="w-4 h-4 text-primary" />
                        Blog CMS
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="cursor-pointer text-muted-foreground"
                    data-testid="button-logout"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    {ui.signOut}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                variant="default"
                size="sm"
                className="hidden sm:flex rounded-full px-3 shadow-sm font-semibold"
                onClick={() => setAuthModalOpen(true)}
                data-testid="button-sign-in"
              >
                <UserCircle className="w-4 h-4 mr-1.5" />
                {ui.signIn}
              </Button>
            )}

            {/* Mobile menu toggle — always last, hidden on xl */}
            <Button
              variant="ghost"
              size="icon"
              className="xl:hidden rounded-full w-8 h-8 text-foreground hover:bg-muted"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? ui.closeMenu : ui.openMenu}
              aria-expanded={mobileOpen}
              data-testid="button-mobile-menu"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="xl:hidden border-t border-border/40 bg-background/98 backdrop-blur-xl overflow-y-auto max-h-[calc(100dvh-56px)] relative">
            <div className="container mx-auto px-4 py-3 pb-10">
              {/* Primary nav group */}
              <p className="px-3 pb-1 pt-0.5 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                {PRIMARY_GROUP_LABEL[language] ?? PRIMARY_GROUP_LABEL.en}
              </p>
              <div className="flex flex-col gap-0.5 mb-3">
                {PRIMARY_NAV.map((link) => {
                  const Icon = link.icon;
                  const href = localePath(link.href, language);
                  const active = isActive(link.href);
                  return (
                    <Link
                      key={link.href}
                      href={href}
                      onClick={() => setMobileOpen(false)}
                      onMouseEnter={() => prefetchRoute(link.href)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                        active
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      }`}
                      data-testid={`${link.testId}-mobile`}
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      {link.labels[language] ?? link.labels.en}
                    </Link>
                  );
                })}
              </div>
              {/* More nav group */}
              <p className="px-3 pb-1 pt-1 border-t border-border/30 text-[10px] font-bold text-muted-foreground uppercase tracking-widest mt-1">
                {MORE_GROUP_LABEL[language] ?? MORE_GROUP_LABEL.en}
              </p>
              <div className="flex flex-col gap-0.5 mb-3">
                {MORE_NAV.map((link) => {
                  const Icon = link.icon;
                  const href = localePath(link.href, language);
                  const active = isActive(link.href);
                  return (
                    <Link
                      key={link.href}
                      href={href}
                      onClick={() => setMobileOpen(false)}
                      onMouseEnter={() => prefetchRoute(link.href)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                        active
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      }`}
                      data-testid={`${link.testId}-mobile`}
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      {link.labels[language] ?? link.labels.en}
                    </Link>
                  );
                })}
              </div>

              {/* Bottom section: auth + language */}
              <div className="border-t border-border/40 pt-3 flex flex-col gap-0.5">
                {user ? (
                  <>
                    <div className="px-3 py-1 mb-1">
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                    <Link
                      href={localePath("/favorites", language)}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                      data-testid="link-nav-favorites-mobile"
                    >
                      <Heart className="w-4 h-4 text-red-500" />
                      {ui.myFavourites}
                    </Link>
                    {user.role === "admin" && (
                      <>
                        <Link
                          href="/admin"
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                          data-testid="link-nav-admin-mobile"
                        >
                          <ShieldCheck className="w-4 h-4" />
                          Admin Panel
                        </Link>
                        <Link
                          href="/ai-cms/dashboard"
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-primary hover:bg-primary/10"
                          data-testid="link-nav-cms-mobile"
                        >
                          <BookOpen className="w-4 h-4" />
                          Blog CMS
                        </Link>
                      </>
                    )}
                    <button
                      onClick={() => { handleLogout(); setMobileOpen(false); }}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground w-full text-left"
                      data-testid="button-logout-mobile"
                    >
                      <LogOut className="w-4 h-4" />
                      {ui.signOut}
                    </button>
                  </>
                ) : (
                  <Button
                    variant="default"
                    className="w-full rounded-md font-semibold"
                    onClick={() => { setAuthModalOpen(true); setMobileOpen(false); }}
                    data-testid="button-sign-in-mobile"
                  >
                    <UserCircle className="w-4 h-4 mr-2" />
                    {ui.signIn}
                  </Button>
                )}

                {/* Language picker in mobile */}
                <div className="mt-2 pt-2 border-t border-border/40">
                  <p className="px-3 pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Language</p>
                  <div className="flex flex-wrap gap-1 px-2">
                    {LANG_OPTIONS.map((opt) => (
                      <button
                        key={opt.code}
                        onClick={() => { handleLanguageSwitch(opt.code); setMobileOpen(false); }}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                          language === opt.code
                            ? "bg-primary text-primary-foreground"
                            : "text-muted-foreground hover:bg-muted hover:text-foreground"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>

      {authModalOpen && (
        <Suspense fallback={null}>
          <AuthModal open={authModalOpen} onClose={() => setAuthModalOpen(false)} />
        </Suspense>
      )}
      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
